import { between, createRng, pick, round } from "./rng";

const cubicPoint = ([p0, p1, p2, p3], t) => {
  const u = 1 - t;
  return [0, 1].map(
    (k) => u * u * u * p0[k] + 3 * u * u * t * p1[k] + 3 * u * t * t * p2[k] + t * t * t * p3[k],
  );
};

const curvePath = ([p0, p1, p2, p3]) => `M${p0} C${p1} ${p2} ${p3}`;

export const Leaf = ({ x, y, length, angle, width = 0.38, fill = "url(#rm-leaf)" }) => (
  <g transform={`translate(${round(x)} ${round(y)}) rotate(${round(angle)})`}>
    <path
      d={`M0 0 C${round(length * 0.3)} ${round(-length * width)} ${round(length * 0.75)} ${round(-length * width * 0.8)} ${round(length)} 0 C${round(length * 0.75)} ${round(length * width * 0.8)} ${round(length * 0.3)} ${round(length * width)} 0 0 Z`}
      fill={fill}
    />
    <path d={`M1 0 Q${round(length * 0.5)} ${round(-length * 0.04)} ${round(length * 0.92)} 0`} stroke="#c9c98a" strokeWidth="0.6" fill="none" opacity="0.45" />
  </g>
);

// Bougainvillea flower: three papery bracts around a tiny cream floret.
const Bract = ({ size, angle, fill }) => (
  <path
    transform={`rotate(${round(angle)})`}
    d={`M0 0 C${round(-size * 0.62)} ${round(-size * 0.18)} ${round(-size * 0.66)} ${round(-size * 0.9)} 0 ${round(-size)} C${round(size * 0.66)} ${round(-size * 0.9)} ${round(size * 0.62)} ${round(-size * 0.18)} 0 0 Z`}
    fill={fill}
  />
);

export const Blossom = ({ x, y, size, angle, gradient, delay }) => (
  <g className="rm-bloom" transform={`translate(${round(x)} ${round(y)})`} style={{ "--d": `${round(delay)}s` }}>
    <g className="rm-bloom__inner">
      {[0, 120, 240].map((a) => (
        <Bract key={a} size={size} angle={angle + a} fill={`url(#rm-bract-${gradient})`} />
      ))}
      <circle r={round(size * 0.12)} fill="#fbf1d8" />
    </g>
  </g>
);

// Branch paths are hand-placed to drape from the top-right corner like the card.
const BRANCHES = [
  { c: [[540, 6], [460, 4], [380, 30], [270, 26]], weight: 1 },
  { c: [[540, 36], [470, 60], [400, 90], [330, 150]], weight: 1 },
  { c: [[540, 70], [500, 140], [430, 200], [400, 300]], weight: 0.9 },
  { c: [[520, 0], [470, 120], [470, 250], [440, 380]], weight: 0.8 },
  { c: [[540, 150], [520, 260], [500, 360], [492, 470]], weight: 0.6 },
  { c: [[540, 240], [532, 360], [520, 470], [526, 600]], weight: 0.45 },
  { c: [[470, 0], [400, -6], [330, 6], [200, 0]], weight: 0.8 },
];

/** Cascading crimson bougainvillea, anchored to the top-right corner. */
export const Bougainvillea = ({ className = "", seed = 11, density = 1 }) => {
  const rng = createRng(seed);
  const blossoms = [];
  const leaves = [];

  BRANCHES.forEach(({ c, weight }, b) => {
    const clusters = Math.round(16 * weight * density);
    for (let i = 0; i < clusters; i++) {
      const t = Math.pow(i / clusters, 0.85);
      const [bx, by] = cubicPoint(c, t);
      const spread = 30 * (1 - t * 0.55);
      const flowers = Math.round(between(rng, 2, 5) * (1.2 - t * 0.5));
      for (let f = 0; f < flowers; f++) {
        blossoms.push({
          x: bx + between(rng, -spread, spread),
          y: by + between(rng, -spread * 0.6, spread * 0.9),
          size: between(rng, 8, 14) * (1 - t * 0.3),
          angle: between(rng, 0, 120),
          gradient: Math.floor(rng() * 4),
          delay: 0.3 + b * 0.12 + t * 1.4 + rng() * 0.3,
        });
      }
      if (rng() > 0.35) {
        leaves.push({
          x: bx + between(rng, -spread, spread),
          y: by + between(rng, -spread * 0.4, spread * 0.6),
          length: between(rng, 14, 24),
          angle: between(rng, 40, 160),
          fill: rng() > 0.5 ? "url(#rm-leaf)" : "url(#rm-leaf-dark)",
        });
      }
    }
  });

  return (
    <svg className={className} viewBox="0 0 540 620" preserveAspectRatio="xMaxYMin meet" aria-hidden="true">
      <g filter="url(#rm-watercolor)">
        {BRANCHES.map(({ c }, i) => (
          <path key={i} d={curvePath(c)} fill="none" stroke="#6b4a2a" strokeWidth={i < 3 ? 2.4 : 1.4} strokeLinecap="round" opacity="0.75" />
        ))}
        {leaves.map((leaf, i) => (
          <Leaf key={`l${i}`} {...leaf} />
        ))}
        {blossoms.map((b, i) => (
          <Blossom key={`b${i}`} {...b} />
        ))}
      </g>
    </svg>
  );
};

/** A rose seen from above: three rings of petals around a curled heart. */
export const Rose = ({ x, y, r, palette = "crimson", seed = 1, tilt = 0.86 }) => {
  const rng = createRng(seed);
  const fill = `url(#rm-rose-${palette})`;
  const ring = (count, dist, len, wid, offset) =>
    Array.from({ length: count }, (_, i) => {
      const angle = offset + (360 / count) * i + between(rng, -10, 10);
      const L = len * between(rng, 0.9, 1.08);
      const W = wid * between(rng, 0.9, 1.1);
      return (
        <path
          key={`${dist}-${i}`}
          transform={`rotate(${round(angle)}) translate(0 ${round(-dist)})`}
          d={`M0 ${round(L * 0.25)} C${round(-W)} ${round(L * 0.05)} ${round(-W * 0.85)} ${round(-L * 0.85)} 0 ${round(-L * 0.72)} C${round(W * 0.85)} ${round(-L * 0.85)} ${round(W)} ${round(L * 0.05)} 0 ${round(L * 0.25)} Z`}
          fill={fill}
          stroke="#ffffff"
          strokeOpacity="0.18"
          strokeWidth="0.8"
        />
      );
    });
  return (
    <g transform={`translate(${round(x)} ${round(y)}) scale(1 ${tilt})`}>
      {ring(6, r * 0.42, r * 0.62, r * 0.5, between(rng, 0, 60))}
      {ring(5, r * 0.22, r * 0.5, r * 0.4, between(rng, 0, 72))}
      {ring(4, r * 0.08, r * 0.36, r * 0.3, between(rng, 0, 90))}
      <path
        d={`M${round(-r * 0.16)} 0 C${round(-r * 0.16)} ${round(-r * 0.2)} ${round(r * 0.18)} ${round(-r * 0.2)} ${round(r * 0.16)} 0 C${round(r * 0.14)} ${round(r * 0.14)} ${round(-r * 0.08)} ${round(r * 0.12)} ${round(-r * 0.04)} ${round(-r * 0.04)}`}
        fill="none"
        stroke="#3a0a10"
        strokeOpacity="0.45"
        strokeWidth={round(Math.max(0.8, r * 0.035))}
        strokeLinecap="round"
      />
    </g>
  );
};

/** Baby's breath / jasmine sprig: branching stems ending in tiny white florets. */
export const Sprig = ({ x, y, length, angle, seed = 3 }) => {
  const rng = createRng(seed);
  const tips = Array.from({ length: 7 }, (_, i) => {
    const t = 0.35 + (i / 7) * 0.65;
    const side = i % 2 === 0 ? -1 : 1;
    return {
      sx: length * t,
      tx: length * t + between(rng, 6, 16),
      ty: side * between(rng, 6, 18),
    };
  });
  return (
    <g transform={`translate(${round(x)} ${round(y)}) rotate(${round(angle)})`}>
      <path d={`M0 0 Q${round(length * 0.5)} -6 ${round(length)} 0`} stroke="#6f7a3a" strokeWidth="1" fill="none" />
      {tips.map(({ sx, tx, ty }, i) => (
        <g key={i}>
          <line x1={round(sx)} y1="0" x2={round(tx)} y2={round(ty)} stroke="#6f7a3a" strokeWidth="0.7" />
          {[0, 1, 2].map((k) => (
            <circle
              key={k}
              cx={round(tx + between(rng, -3.5, 3.5))}
              cy={round(ty + between(rng, -3.5, 3.5))}
              r={round(between(rng, 1.6, 2.8))}
              fill="#fdf8ee"
              stroke="#e4d6bc"
              strokeWidth="0.4"
            />
          ))}
        </g>
      ))}
    </g>
  );
};

const ROSES_LAYOUT = [
  { x: 74, y: 198, r: 46, palette: "wine", seed: 2 },
  { x: 150, y: 258, r: 56, palette: "crimson", seed: 3 },
  { x: 40, y: 280, r: 44, palette: "crimson", seed: 4 },
  { x: 228, y: 292, r: 40, palette: "blush", seed: 5 },
  { x: 168, y: 176, r: 34, palette: "peach", seed: 6 },
  { x: 290, y: 320, r: 30, palette: "blush", seed: 7 },
  { x: 102, y: 330, r: 36, palette: "peach", seed: 8 },
];

/** Lush corner bouquet of roses, baby's breath and leaves (bottom-left; mirror for right). */
export const RoseCorner = ({ className = "", mirror = false, seed = 21 }) => {
  const rng = createRng(seed);
  const leaves = Array.from({ length: 34 }, () => ({
    x: between(rng, 0, 260),
    y: between(rng, 150, 350),
    length: between(rng, 26, 46),
    angle: between(rng, -170, -10),
    fill: pick(rng, ["url(#rm-leaf)", "url(#rm-leaf-dark)"]),
  }));
  const sprigs = Array.from({ length: 7 }, (_, i) => ({
    x: between(rng, 10, 240),
    y: between(rng, 190, 300),
    length: between(rng, 60, 110),
    angle: between(rng, -150, -30),
    seed: seed + i,
  }));
  return (
    <svg
      className={className}
      viewBox="0 0 360 360"
      preserveAspectRatio={mirror ? "xMaxYMax meet" : "xMinYMax meet"}
      aria-hidden="true"
    >
      <g transform={mirror ? "translate(360 0) scale(-1 1)" : undefined} filter="url(#rm-watercolor)">
        {leaves.map((leaf, i) => (
          <Leaf key={i} {...leaf} />
        ))}
        {sprigs.slice(0, 4).map((s, i) => (
          <Sprig key={`s${i}`} {...s} />
        ))}
        {ROSES_LAYOUT.map((rose) => (
          <Rose key={rose.seed} {...rose} seed={rose.seed + seed} />
        ))}
        {sprigs.slice(4).map((s, i) => (
          <Sprig key={`t${i}`} {...s} />
        ))}
      </g>
    </svg>
  );
};
